import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { DEFAULT_SEED, createSeededRuntime } from "@/lib/seeded";
import { createYJApi, type YJApi } from "@/domain/yjmall/api";
import { createScenarioState } from "@/domain/yjmall/scenarios";
import type { YJMockData } from "@/domain/yjmall/seed";
import type { BrandId, BrokerId, ScenarioId } from "@/domain/yjmall/types";

export const YJMALL_PERSIST_KEY = "yjmall_redesign_mock_v1";
export const YJMALL_PERSIST_VERSION = 1;

type YJStore = YJMockData & {
  api: YJApi;
  reset: () => void;
  loadScenario: (scenario: ScenarioId) => void;
  setCurrentBrokerId: (id: BrokerId) => void;
  setCurrentBrandId: (id: BrandId) => void;
};

const runtime = createSeededRuntime(`${DEFAULT_SEED}:api`);

function defaultData() {
  return createScenarioState("normal");
}

function mergePersistedData(persisted: Partial<YJMockData> | undefined): YJMockData {
  const base = defaultData();
  if (!persisted) return base;
  return {
    ...base,
    ...persisted,
    settings: { ...base.settings, ...persisted.settings },
    clock: { ...base.clock, ...persisted.clock },
    counters: { ...base.counters, ...persisted.counters },
    scenarioConfig: { ...base.scenarioConfig, ...persisted.scenarioConfig },
  };
}

function dataOnly(state: YJStore): YJMockData {
  return {
    brands: state.brands,
    products: state.products,
    brokers: state.brokers,
    customers: state.customers,
    orders: state.orders,
    leads: state.leads,
    tickets: state.tickets,
    coupons: state.coupons,
    campaigns: state.campaigns,
    settlements: state.settlements,
    supplierPayouts: state.supplierPayouts,
    affiliateTemplates: state.affiliateTemplates,
    flaggedPosts: state.flaggedPosts,
    pushLog: state.pushLog,
    events: state.events,
    currentBrokerId: state.currentBrokerId,
    currentBrandId: state.currentBrandId,
    currentScenario: state.currentScenario,
    settings: state.settings,
    clock: state.clock,
    counters: state.counters,
    scenarioConfig: state.scenarioConfig,
  };
}

export const useYJMockStore = create<YJStore>()(
  persist(
    (set, get) => {
      const api = createYJApi({
        getState: () => dataOnly(get()),
        setState: (data) => set(data),
        runtime,
      });

      return {
        ...defaultData(),
        api,
        reset: () => set(defaultData()),
        loadScenario: (scenario) => set(createScenarioState(scenario)),
        setCurrentBrokerId: (id) => set({ currentBrokerId: id }),
        setCurrentBrandId: (id) => set({ currentBrandId: id }),
      };
    },
    {
      name: YJMALL_PERSIST_KEY,
      version: YJMALL_PERSIST_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => dataOnly(state),
      migrate: (persistedState) => mergePersistedData(persistedState as Partial<YJMockData> | undefined),
    },
  ),
);

export function useYJApi() {
  return useYJMockStore((state) => state.api);
}

export function useYJState(): YJMockData;
export function useYJState<T>(selector: (state: YJMockData) => T): T;
export function useYJState<T>(selector?: (state: YJMockData) => T) {
  return useYJMockStore((state) => {
    const data = dataOnly(state);
    return selector ? selector(data) : data;
  });
}

export function getYJStateSnapshot() {
  return dataOnly(useYJMockStore.getState());
}
